import { z } from "zod";
import { apiBuilder, prefixApi } from "@zodios/core";
import { datasetFilterSchema, dspDatasetsPaginationSchema, dspFrequencySchema, parseJson } from "./common";

const dspBidSchema = z.object({
  baseBid: z.number().optional(),
  currencyCode: z.string().optional(),
  maxAverageCPM: z.number().optional(),
});

const dspPacingSchema = z.object({
  catchUpBoostPercentage: z.number().optional(),
  deliveryProfile: z.string().optional(),
});

const dspTargetingSettingsSchema = z.object({
  amazonViewability: z
    .object({
      includeUnmeasurableImpressions: z.boolean().optional(),
      viewabilityTier: z.string().optional(),
    })
    .optional(),
  automatedTargetingTactic: z.string().optional(),
  defaultAudienceTargetingMatchType: z.string().optional(),
  enableLanguageTargeting: z.boolean().optional(),
  tacticsConvertersExclusionType: z.string().optional(),
  timeZoneType: z.string().optional(),
  userLocation: z.string().optional(),
  userLocationSignal: z.string().optional(),
});

const dspLineItemOptimizationSchema = z.object({
  automateBidAllocation: z.boolean().optional(),
  automateBudgetAllocation: z.boolean().optional(),
  bidStrategy: z.string().optional(),
  dailyMinSpendAmount: z.number().optional(),
});

const dspLineItemEntityFields = {
  ad_group_id: z.union([z.string(), z.number()]).optional(),
  line_item_id: z.union([z.string(), z.number()]).optional(),
  id: z.number().optional(),
  name: z.string().optional(),
  state: z.string().optional(),
  delivery_status: z.string().optional(),
  start_date_time: z.string().optional(),
  end_date_time: z.string().optional(),
  creation_date_time: z.string().optional(),
  last_updated_date_time: z.string().optional(),
  campaign_id: z.string().optional(),
  advertiser_id: z.string().optional(),
  order_id: z.union([z.string(), z.number()]).optional(),
  inventory_type: z.string().optional(),
  comments: z.string().optional(),
  frequencies: z.string().optional(),
  optimization: z.string().optional(),
  bid: z.string().optional(),
  fees: z.string().optional(),
  budget_caps: z.string().optional(),
  budget_amount: z.number().nullable().optional(),
  pacing: z.string().optional(),
  targeting_settings: z.string().optional(),
  advertised_product_category_ids: z.string().optional(),
  purchase_order_number: z.string().optional(),
  base_bid: z.number().nullable().optional(),
  supply_source_type: z.string().optional(),
  creative_rotation_type: z.string().optional(),
  program_name: z.string().nullable().optional(),
  start_date: z.string().optional(),
  day: z.string().optional(),
  week: z.string().optional(),
  month: z.string().optional(),
};

const dspLineItemMetricFields = {
  impressions: z.number().nullable(),
  impressions_prev: z.number().nullable().optional(),
  impressions_delta: z.number().nullable().optional(),
  impressions_percent: z.number().nullable().optional(),

  clicks: z.number().nullable(),
  clicks_prev: z.number().nullable().optional(),
  clicks_delta: z.number().nullable().optional(),
  clicks_percent: z.number().nullable().optional(),

  sales: z.number().nullable(),
  sales_prev: z.number().nullable().optional(),
  sales_delta: z.number().nullable().optional(),
  sales_percent: z.number().nullable().optional(),

  total_sales: z.number().nullable(),
  total_sales_prev: z.number().nullable().optional(),
  total_sales_delta: z.number().nullable().optional(),
  total_sales_percent: z.number().nullable().optional(),

  total_cost: z.number().nullable(),
  total_cost_prev: z.number().nullable().optional(),
  total_cost_delta: z.number().nullable().optional(),
  total_cost_percent: z.number().nullable().optional(),

  total_detail_page_view: z.number().nullable(),
  total_detail_page_view_prev: z.number().nullable().optional(),
  total_detail_page_view_delta: z.number().nullable().optional(),
  total_detail_page_view_percent: z.number().nullable().optional(),

  total_purchases: z.number().nullable(),
  total_purchases_prev: z.number().nullable().optional(),
  total_purchases_delta: z.number().nullable().optional(),
  total_purchases_percent: z.number().nullable().optional(),

  brand_searches: z.number().nullable(),
  brand_searches_prev: z.number().nullable().optional(),
  brand_searches_delta: z.number().nullable().optional(),
  brand_searches_percent: z.number().nullable().optional(),

  new_to_brand_detail_page_view: z.number().nullable().optional(),
  new_to_brand_detail_page_view_prev: z.number().nullable().optional(),
  new_to_brand_detail_page_view_delta: z.number().nullable().optional(),
  new_to_brand_detail_page_view_percent: z.number().nullable().optional(),

  total_add_to_cart: z.number().nullable(),
  total_add_to_cart_prev: z.number().nullable().optional(),
  total_add_to_cart_delta: z.number().nullable().optional(),
  total_add_to_cart_percent: z.number().nullable().optional(),

  total_new_to_brand_dpvs: z.number().nullable(),
  total_new_to_brand_dpvs_prev: z.number().nullable().optional(),
  total_new_to_brand_dpvs_delta: z.number().nullable().optional(),
  total_new_to_brand_dpvs_percent: z.number().nullable().optional(),

  viewable_impressions: z.number().nullable(),
  viewable_impressions_prev: z.number().nullable().optional(),
  viewable_impressions_delta: z.number().nullable().optional(),
  viewable_impressions_percent: z.number().nullable().optional(),

  units_sold: z.number().nullable(),
  units_sold_prev: z.number().nullable().optional(),
  units_sold_delta: z.number().nullable().optional(),
  units_sold_percent: z.number().nullable().optional(),

  total_units_sold: z.number().nullable(),
  total_units_sold_prev: z.number().nullable().optional(),
  total_units_sold_delta: z.number().nullable().optional(),
  total_units_sold_percent: z.number().nullable().optional(),

  ecpm: z.number().nullable(),
  ecpm_prev: z.number().nullable().optional(),
  ecpm_delta: z.number().nullable().optional(),
  ecpm_percent: z.number().nullable().optional(),

  click_thru_rate: z.number().nullable(),
  click_thru_rate_prev: z.number().nullable().optional(),
  click_thru_rate_delta: z.number().nullable().optional(),
  click_thru_rate_percent: z.number().nullable().optional(),

  tacos: z.number().nullable(),
  tacos_prev: z.number().nullable().optional(),
  tacos_delta: z.number().nullable().optional(),
  tacos_percent: z.number().nullable().optional(),

  troas: z.number().nullable(),
  troas_prev: z.number().nullable().optional(),
  troas_delta: z.number().nullable().optional(),
  troas_percent: z.number().nullable().optional(),

  roas: z.number().nullable(),
  roas_prev: z.number().nullable().optional(),
  roas_delta: z.number().nullable().optional(),
  roas_percent: z.number().nullable().optional(),

  total_detail_page_view_rate: z.number().nullable(),
  total_detail_page_view_rate_prev: z.number().nullable().optional(),
  total_detail_page_view_rate_delta: z.number().nullable().optional(),
  total_detail_page_view_rate_percent: z.number().nullable().optional(),

  total_add_to_cart_rate: z.number().nullable(),
  total_add_to_cart_rate_prev: z.number().nullable().optional(),
  total_add_to_cart_rate_delta: z.number().nullable().optional(),
  total_add_to_cart_rate_percent: z.number().nullable().optional(),

  new_to_brand_detail_page_view_rate: z.number().nullable().optional(),
  new_to_brand_detail_page_view_rate_prev: z.number().nullable().optional(),
  new_to_brand_detail_page_view_rate_delta: z.number().nullable().optional(),
  new_to_brand_detail_page_view_rate_percent: z.number().nullable().optional(),

  total_new_to_brand_dpv_rate: z.number().nullable(),
  total_new_to_brand_dpv_rate_prev: z.number().nullable().optional(),
  total_new_to_brand_dpv_rate_delta: z.number().nullable().optional(),
  total_new_to_brand_dpv_rate_percent: z.number().nullable().optional(),

  brand_searches_rate: z.number().nullable(),
  brand_searches_rate_prev: z.number().nullable().optional(),
  brand_searches_rate_delta: z.number().nullable().optional(),
  brand_searches_rate_percent: z.number().nullable().optional(),

  cost_per_detail_page_view: z.number().nullable(),
  cost_per_detail_page_view_prev: z.number().nullable().optional(),
  cost_per_detail_page_view_delta: z.number().nullable().optional(),
  cost_per_detail_page_view_percent: z.number().nullable().optional(),

  viewability: z.number().nullable(),
  viewability_prev: z.number().nullable().optional(),
  viewability_delta: z.number().nullable().optional(),
  viewability_percent: z.number().nullable().optional(),
};

const dspLineItemRowSchema = z
  .object({
    ...dspLineItemEntityFields,
    ...dspLineItemMetricFields,
  })
  .transform((row) => {
    return {
      ...row,
      optimization: parseJson(dspLineItemOptimizationSchema, row.optimization ?? "", null),
      bid: parseJson(dspBidSchema, row.bid ?? "", null),
      fees: parseJson(z.array(z.unknown()), row.fees ?? "", []),
      budget_caps: parseJson(z.array(z.unknown()), row.budget_caps ?? "", []),
      frequencies: parseJson(z.array(dspFrequencySchema), row.frequencies ?? "", []),
      pacing: parseJson(dspPacingSchema, row.pacing ?? "", null),
      targeting_settings: parseJson(dspTargetingSettingsSchema, row.targeting_settings ?? "", null),
      advertised_product_category_ids: parseJson(z.array(z.string()), row.advertised_product_category_ids ?? "", []),
    };
  });

export type DSPLineItemRow = z.infer<typeof dspLineItemRowSchema>;

const dspLineItemQueryRequestSchema = z.object({
  model: z.literal("dsplineitem"),
  dimensions: z.union([z.string(), z.array(z.string())]),
  entity_mode: z.union([z.literal("none"), z.literal("join"), z.literal("all"), z.literal("without_stats")]),
  filters: z.array(datasetFilterSchema).optional().default([]),
  sort: z.string().optional(),
  page: z.number().optional(),
  limit: z.number().optional(),
  include_count: z.boolean().optional(),
});

const dspLineItemQueryResponseSchema = z.object({
  data: z.array(dspLineItemRowSchema),
  pagination: dspDatasetsPaginationSchema,
});

export const dspLineItemDatasetsApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    method: "post",
    path: "/datasets/query/?lineitem",
    alias: "dspLineItemDatasetsQuery",
    description: "Query line item datasets by dimensions + filters",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: dspLineItemQueryRequestSchema,
      },
    ],
    response: dspLineItemQueryResponseSchema,
  }).build(),
);
